import { formatDateToApi } from '@/utils/format';

/**
 * List state (search, filters, sort, page) kept in the URL query, so a refresh
 * or a return from a detail page lands on the same list. Pure functions only;
 * `useUrlListState` wires them to the router.
 *
 * Each key's type is taken from its default:
 *   number   → `page=3`; anything not a whole number falls back to the default
 *   boolean  → `arrearsSort=false`
 *   string[] → `oemIDs=a,b`; `oemIDs=` is an explicit empty list, which differs
 *              from an absent key whenever the default is not empty
 *   string   → `q=B4776`
 *   null     → an optional string (a date, a bucket); absent or empty is null
 *
 * A value equal to its default is never written, so an untouched list keeps a
 * clean URL and only what the user changed shows up in it.
 */

export type UrlValue = string | number | boolean | string[] | null;
export type UrlSchema = Record<string, UrlValue>;
export type UrlValidators<T extends UrlSchema> = {
  [K in keyof T]?: (value: T[K]) => boolean;
};

const parseOne = (raw: string, fallback: UrlValue): UrlValue | undefined => {
  if (Array.isArray(fallback)) {
    return raw === '' ? [] : raw.split(',').filter(Boolean);
  }
  if (typeof fallback === 'number') {
    return /^-?\d+$/.test(raw) ? Number(raw) : undefined;
  }
  if (typeof fallback === 'boolean') {
    if (raw === 'true') return true;
    if (raw === 'false') return false;
    return undefined;
  }
  if (fallback === null) return raw === '' ? null : raw;
  return raw;
};

const sameValue = (a: UrlValue, b: UrlValue): boolean => {
  if (Array.isArray(a) && Array.isArray(b)) {
    // Order carries no meaning in a multi-select, so [b, a] is still the default.
    return (
      a.length === b.length && [...a].sort().join() === [...b].sort().join()
    );
  }
  return a === b;
};

export const readUrlState = <T extends UrlSchema>(
  params: URLSearchParams,
  defaults: T,
  validators: UrlValidators<T> = {}
): T => {
  const state = { ...defaults };
  (Object.keys(defaults) as (keyof T & string)[]).forEach((key) => {
    const raw = params.get(key);
    if (raw === null) return;
    const parsed = parseOne(raw, defaults[key]);
    if (parsed === undefined) return;
    const valid = validators[key];
    if (valid && !valid(parsed as T[typeof key])) return;
    state[key] = parsed as T[typeof key];
  });
  return state;
};

/** Applies `patch` on top of `params`; keys outside the schema are kept. */
export const writeUrlState = <T extends UrlSchema>(
  params: URLSearchParams,
  patch: Partial<T>,
  defaults: T
): URLSearchParams => {
  const next = new URLSearchParams(params);
  (Object.keys(patch) as (keyof T & string)[]).forEach((key) => {
    if (!(key in defaults)) return;
    const value = patch[key] as UrlValue;
    // null only ever means "unset" for an optional key, so it reads back as
    // the default either way.
    if (
      value === undefined ||
      value === null ||
      sameValue(value, defaults[key])
    ) {
      next.delete(key);
    } else {
      next.set(key, Array.isArray(value) ? value.join(',') : String(value));
    }
  });
  return next;
};

/** True when any key outside `ignore` differs from its default. */
export const isUrlStateDirty = <T extends UrlSchema>(
  state: T,
  defaults: T,
  ignore: (keyof T)[] = []
): boolean =>
  (Object.keys(defaults) as (keyof T)[]).some(
    (key) => !ignore.includes(key) && !sameValue(state[key], defaults[key])
  );

/** Every key outside `keep` back to its default. */
export const resetPatch = <T extends UrlSchema>(
  defaults: T,
  keep: (keyof T)[] = []
): Partial<T> => {
  const patch: Partial<T> = {};
  (Object.keys(defaults) as (keyof T)[]).forEach((key) => {
    if (!keep.includes(key)) patch[key] = defaults[key];
  });
  return patch;
};

/**
 * The last page that still has rows. A restored `page=5` on a list that has
 * shrunk to two pages would otherwise show an empty table with no way back.
 */
export const clampPage = (
  page: number,
  total: number,
  size: number
): number => {
  if (total <= 0 || size <= 0) return 1;
  return Math.min(Math.max(page, 1), Math.ceil(total / size));
};

/**
 * UniversalFilter's `currentFilters`: only the keys that hold a value, the
 * shape the pages built their API params from before the state moved here.
 */
export const pickFilters = <T extends UrlSchema>(
  state: T,
  keys: readonly (keyof T & string)[]
  // `any` like the `activeFilters` state the pages held before: their param
  // builders read both list and scalar filters off the same object.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Record<string, any> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const filters: Record<string, any> = {};
  keys.forEach((key) => {
    const value = state[key];
    if (
      Array.isArray(value)
        ? value.length > 0
        : typeof value === 'string' && value !== ''
    ) {
      filters[key] = value;
    }
  });
  return filters;
};

/** The reverse of `pickFilters`: a key the filter dropped goes back to its default. */
export const filtersPatch = <T extends UrlSchema>(
  filters: Record<string, unknown>,
  keys: readonly (keyof T & string)[],
  defaults: T
): Partial<T> => {
  const patch: Partial<T> = {};
  keys.forEach((key) => {
    const value = filters[key];
    if (value === undefined || value === null) {
      patch[key] = (Array.isArray(defaults[key]) ? [] : '') as T[typeof key];
    } else if (Array.isArray(value)) {
      patch[key] = value.map(String) as T[typeof key];
    } else {
      patch[key] = String(value) as T[typeof key];
    }
  });
  return patch;
};

const DATE_PARAM = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A calendar date as `YYYY-MM-DD`, in local time like the date pickers. */
export const toDateParam = (date: Date | null): string | null =>
  date && !Number.isNaN(date.getTime()) ? formatDateToApi(date) : null;

/** Null for anything that is not a real calendar date (2026-02-30 included). */
export const fromDateParam = (value: string | null): Date | null => {
  const match = value ? DATE_PARAM.exec(value) : null;
  if (!match) return null;
  const [, y, m, d] = match.map(Number);
  const date = new Date(y, m - 1, d);
  return date.getFullYear() === y &&
    date.getMonth() === m - 1 &&
    date.getDate() === d
    ? date
    : null;
};

export const isDateParam = (value: string | null): boolean =>
  value === null || fromDateParam(value) !== null;

/** Router state a list hands to its detail page, so Back keeps the query. */
export type ListReturnState = { from?: string };

export const listReturnState = (location: {
  pathname: string;
  search: string;
}): ListReturnState => ({ from: `${location.pathname}${location.search}` });

/**
 * Where a detail page's Back button goes: the list URL it was opened from when
 * that is the expected list, otherwise the plain list path. The prefix check
 * keeps a stale or foreign `from` (another page's state) from sending Back
 * somewhere unrelated.
 */
export const listReturnPath = (state: unknown, fallback: string): string => {
  const from = (state as ListReturnState | null)?.from;
  if (typeof from !== 'string') return fallback;
  return from === fallback || from.startsWith(`${fallback}?`) ? from : fallback;
};

/** The page sizes Pagination offers; any other `size` in the URL is ignored. */
export const PAGE_SIZE_OPTIONS = [10, 20, 30, 40, 50];

/**
 * The direction a sort header click asks for: an explicit direction wins,
 * otherwise the current column toggles and a new column starts ascending.
 */
export const nextSortDirection = (
  field: string,
  current: { field: string; direction: 'asc' | 'desc' | null },
  explicit?: 'asc' | 'desc' | null
): 'asc' | 'desc' => {
  if (explicit) return explicit;
  if (current.field !== field) return 'asc';
  return current.direction === 'asc' ? 'desc' : 'asc';
};

/** The keys every filtered list shares, plus the page's own (filters, extras). */
export const listDefaults = <E extends UrlSchema>(extra: E) => ({
  q: '',
  page: 1,
  size: 10,
  sort: '',
  dir: null as string | null,
  from: null as string | null,
  to: null as string | null,
  ...extra,
});

export type ListState = UrlSchema & {
  q: string;
  page: number;
  size: number;
  sort: string;
  dir: string | null;
  from: string | null;
  to: string | null;
};

export const LIST_VALIDATORS = {
  page: (v: number) => v >= 1,
  size: (v: number) => PAGE_SIZE_OPTIONS.includes(v),
  dir: (v: string | null) => v === null || v === 'asc' || v === 'desc',
  from: isDateParam,
  to: isDateParam,
};
